import {
  type ReportTextStyle,
  resolveReportTextStyle,
} from "@/components/ui/report/reportPresentation";
import { REPORT_CSS_VAR_PRIMARY } from "@/components/ui/report/reportTheme";
import { scrollToReportScrollTarget } from "@/lib/template-studio/reportScrollTarget";
import { cn } from "@/lib/utils";

export const REPORT_TABLE_OF_CONTENTS_STYLES = ["list", "inline"] as const;

export type ReportTableOfContentsStyle =
  (typeof REPORT_TABLE_OF_CONTENTS_STYLES)[number];

export const REPORT_TABLE_OF_CONTENTS_SEPARATORS = [
  "pipe",
  "bullet",
  "dot",
] as const;

export type ReportTableOfContentsSeparator =
  (typeof REPORT_TABLE_OF_CONTENTS_SEPARATORS)[number];

/** Separator box width in ems of the separator font; 1.15em keeps the original 16px at the default 14px. */
const SEPARATOR_WIDTH_EM = 1.15;

const SEPARATOR_GLYPHS: Record<ReportTableOfContentsSeparator, string> = {
  pipe: "|",
  bullet: "•",
  dot: "·",
};

export interface ReportTableOfContentsEntry {
  elementId: string;
  title: string;
}

export interface ReportTableOfContentsProps {
  sections: ReportTableOfContentsEntry[];
  /** list (default) is the vertical Contents list; inline is one wrapping line of section links. */
  style?: ReportTableOfContentsStyle;
  /** Glyph between inline links. */
  separator?: ReportTableOfContentsSeparator;
  /** Defaults to true for list and false for inline. */
  numbered?: boolean;
  /** Section title typography; omitted keys keep the theme font, and the theme primary colour for inline links. */
  linkStyle?: ReportTextStyle;
  /** Typography for the inline separators. */
  separatorStyle?: ReportTextStyle;
  className?: string;
}

const formatOrderLabel = (order: number): string =>
  String(order).padStart(2, "0");

const ORDER_LABEL_CLASS_NAME =
  "shrink-0 text-[11px] font-normal uppercase leading-4 tracking-[0.44px] text-slate-500";

/** In-report contents list with links to report section blocks. */
export const ReportTableOfContents = ({
  sections,
  style = "list",
  separator = "pipe",
  numbered = style === "list",
  linkStyle,
  separatorStyle,
  className,
}: ReportTableOfContentsProps) => {
  if (style === "inline") {
    // Sized from the separator's own font so the clip below still hides a large line-start separator.
    const separatorWidth =
      separatorStyle?.fontSizePx != null
        ? `${separatorStyle.fontSizePx * SEPARATOR_WIDTH_EM}px`
        : `${SEPARATOR_WIDTH_EM}em`;
    return (
      <nav aria-label="Contents" className={cn("w-full px-4", className)}>
        {/* Every link carries a leading separator; the negative margin and clip hide the one that starts each line. */}
        <div className="overflow-hidden">
          <p
            className="flex flex-wrap gap-y-1 text-sm font-medium leading-5"
            style={{ marginLeft: `calc(-1 * ${separatorWidth})` }}
          >
            {sections.map((section, index) => (
              <span key={section.elementId} className="flex min-w-0">
                <span
                  aria-hidden
                  className="shrink-0 text-center text-gray-400"
                  style={{
                    ...resolveReportTextStyle(separatorStyle),
                    width: separatorWidth,
                  }}
                >
                  {SEPARATOR_GLYPHS[separator]}
                </span>
                <button
                  type="button"
                  className="flex min-w-0 items-baseline gap-1 text-left underline-offset-2 hover:underline"
                  onClick={() => scrollToReportScrollTarget(section.elementId)}
                >
                  {numbered ? (
                    <span className={ORDER_LABEL_CLASS_NAME}>
                      {formatOrderLabel(index + 1)}
                    </span>
                  ) : null}
                  <span
                    className="min-w-0 wrap-break-word"
                    style={{
                      color: `var(${REPORT_CSS_VAR_PRIMARY})`,
                      ...resolveReportTextStyle(linkStyle),
                    }}
                  >
                    {section.title}
                  </span>
                </button>
              </span>
            ))}
          </p>
        </div>
      </nav>
    );
  }

  return (
    <nav
      aria-label="Contents"
      className={cn("flex w-full flex-col gap-2 px-4", className)}
    >
      <p className="text-[11px] font-medium uppercase leading-4 tracking-[0.44px] text-gray-600">
        Contents
      </p>
      {sections.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {sections.map((section, index) => (
            <li key={section.elementId}>
              <button
                type="button"
                className="flex w-full min-w-0 items-end gap-0 text-left"
                onClick={() => scrollToReportScrollTarget(section.elementId)}
              >
                {numbered ? (
                  <span className={cn("w-6", ORDER_LABEL_CLASS_NAME)}>
                    {formatOrderLabel(index + 1)}
                  </span>
                ) : null}
                <span
                  className="min-w-0 wrap-break-word pl-0 text-xs font-medium leading-4 text-gray-700"
                  style={resolveReportTextStyle(linkStyle)}
                >
                  {section.title}
                </span>
                <span
                  aria-hidden
                  className="mb-1 ml-3 min-h-px min-w-8 flex-1 border-b border-dashed border-slate-200"
                />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </nav>
  );
};
